import { useQuery } from '@apollo/client';
import AutoCompleteBrands from '@components/AutoComplete/AutoCompleteBrands';
import { AutoCompleteTalentProfessions } from '@components/AutoComplete/AutoCompleteTalentProfessions';
import InputAutocompleteCity from '@components/InputAutocompleteCity';
import InputDate from '@components/InputDate';
import InputFieldControlled from '@components/InputFieldControlled';
import InputMultiSelect from '@components/InputMultiSelect';
import InputSwitchControl from '@components/InputSwitchControl';
import { VideoContainer } from '@components/InputVideo/VideoContainer';
import Popover from '@components/Popover';
import { GET_BRAND } from '@gql/brand';
import { useOptionsContext } from '@modules/talents/context/options-context';
import { Card, Typography } from '@mui/material';
import { moment } from '@utils/date.utils';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import BrandPhotosPreview from './BrandPhotosPreview';

type Props = {
    pro: any;
};

const PositionForm = ({ pro }: Props) => {
    const { data: optionsData } = useOptionsContext();
    const options = useMemo(() => optionsData?.options, [optionsData]);
    const { control, setValue, register, getValues } = useFormContext();
    const values = getValues();
    const { data: brand } = useQuery(GET_BRAND, {
        variables: { _id: values.brandId },
        skip: !values.brandId,
    });

    const [hasTeam, setHasTeam] = useState(values?.position?.hasTeam);
    const [allowFreshGrad, setAllowFreshGrad] = useState(values?.position?.allowFreshGrad);
    const [enablePublishDate, setEnablePublishDate] = useState(values?.enablePublishDate);

    const [showInfo, setShowInfo] = useState(values.position.companyName);
    const [showPhotos, setShowPhotos] = useState(values.showPhotos);
    const [showVideo, setShowVideo] = useState(values.showVideo);

    const brandPhotos = useMemo(() => {
        if (brand?.brandPhotos?.length > 0) {
            return brand?.brandPhotos;
        }
        return pro?.brandPhotos;
    }, [brand, pro]);

    const brandVideos = useMemo(() => {
        if (brand?.brandVideos?.length > 0) {
            return brand?.brandVideos;
        }
        return pro?.brandVideos;
    }, [brand, pro]);

    const handleChangeInfo = useCallback(
        val => {
            setShowInfo(val);
            setValue('showPhotos', val && brandPhotos?.length > 0);
            setShowPhotos(val && brandPhotos?.length > 0);
            setValue('showVideo', val && !!brandVideos);
            setShowVideo(val && !!brandVideos);
        },
        [brandPhotos?.length, brandVideos, setValue],
    );
    return (
        <>
            <AutoCompleteBrands name="brandId" control={control} label="Marque / Enseigne" proId={pro?._id} />
            <InputFieldControlled
                name="title"
                control={control}
                label="Titre du poste"
                placeholder="Saisissez le titre du poste, la mention H/F sera ajoutée automatiquement"
                endAdornment={
                    <Popover hover>
                        <Typography fontWeight={700}>
                            Titre du poste
                        </Typography>
                        <Typography variant="body2">
                            Saisissez le titre du poste au masculin (ex. : Coiffeur expérimenté). La mention H/F est
                            ajoutée automatiquement.
                        </Typography>
                    </Popover>
                }
                required
            />

            <InputAutocompleteCity
                name="city"
                label="Ville"
                disabled={!pro?.isGroup}
                placeholder="Ex: Paris"
                required
                onSelectProp={function (city: string, postalCode: string): void {
                    // setValue('city', city);
                    setValue('postalCode', postalCode);
                }}
            />
            <InputFieldControlled
                name="address"
                control={control}
                label="Adresse"
                disabled={!pro?.isGroup}
                placeholder="Ex: 12 rue de la paix"
                defaultValue={values?.address}
            />

            <input hidden name="postalCode" {...register('postalCode')} />
            <InputSwitchControl
                control={control}
                name="enablePublishDate"
                defaultValue={values?.enablePublishDate}
                title="Date de publication"
                style={{ alignItems: 'center' }}
                onChange={val => {
                    setEnablePublishDate(val);
                    if (!val) {
                        setValue('publishDate', null);
                    }
                }}
                endAdornment={undefined}
                disabled={undefined}
            />
            {enablePublishDate && (
                <InputDate
                    placeholder="ex : Jeudi 12 janvier 2023"
                    initialDate={values?.publishDate ? moment(values?.publishDate).toDate() : new Date()}
                    minDate={moment(new Date())}
                    name="publishDate"
                    defaultValue={values?.publishDate}
                    control={control}
                    rules={{
                        requiredIfTrue: val => {
                            const isRequired = enablePublishDate === true;
                            return isRequired ? !!val : true;
                        },
                    }}
                />
            )}
            <InputSwitchControl
                control={control}
                name="offre_cachee"
                defaultValue={values?.offre_cachee}
                title="Offre confidentielle"
                style={{ alignItems: 'center' }}
                onChange={val => {
                    if (!val) {
                        setValue('offre_cachee', false);
                    }
                }}
                endAdornment={
                    <Popover hover>
                        <Typography fontWeight={700}>
                            Offre confidentielle
                        </Typography>
                        <Typography variant="body2">
                            En activant le bouton "Offre confidentielle" votre offre ne sera pas diffusée auprès
                            d’Upercut et ses partenaires.
                        </Typography>
                        <Typography variant="body2">
                            Cette option vous permet de créer ici, toutes les offres que vous gérez différemment
                            (chasse de profil, offre discrète, utilisation d’autres sources d’acquisition) pour vous
                            permettre de centraliser dans un même endroit tout votre vivier de candidature.
                        </Typography>
                        <Typography variant="body2">
                            Vous pourrez rattacher à cette offre tous les profil que vous souhaitez parmi ceux que
                            vous avez dans votre vivier.
                        </Typography>
                    </Popover>
                }
                disabled={undefined}
            />
            <InputSwitchControl
                control={control}
                name="position.companyName"
                defaultValue={values?.position?.companyName}
                title="Afficher le nom de l’enseigne"
                disabled={undefined}
                onChange={handleChangeInfo}
                endAdornment={
                    <Popover hover>
                        <Typography fontWeight={700}>
                            Afficher le nom de l’enseigne
                        </Typography>
                        <Typography variant="body2">
                            Lorsque vous décidez d’afficher le nom de l’enseigne, l’introduction de votre offre
                            contiendra le texte que vous avez ajouter dans la section “A propos". Si vous souhaitez
                            le modifier, vous pourrez le faire après avoir finaliser votre offre.
                        </Typography>
                        <Typography variant="body2">
                            Si, vous ne souhaitez pas afficher le nom de votre entreprise, Upercut affichera un
                            texte d’introduction automatiquement.
                        </Typography>
                    </Popover>
                }
            />
            <InputSwitchControl
                control={control}
                name="showPhotos"
                defaultValue={values?.position?.companyName}
                title="Afficher les photos de l’enseigne"
                disabled={brandPhotos?.length === 0 || !showInfo}
                onChange={val => {
                    setShowPhotos(val);
                }}
                endAdornment={
                    <Popover hover>
                        <Typography fontWeight={700}>
                            Afficher les photos de l'enseigne
                        </Typography>
                        <Typography variant="body2">
                            Passez d'une offre classique à une offre augmentée en affichant les photos de votre
                            société.
                        </Typography>
                        <Typography variant="body2">
                            Développez l'intérêt des talents pour votre offre en leurs faisant découvrir leur lieu
                            de travail et les équipes.
                        </Typography>
                        <Typography variant="body2">Une offre augmentée génère jusqu'à 30% de vues en plus.</Typography>
                    </Popover>
                }
            />
            <BrandPhotosPreview photos={brandPhotos} visible={showPhotos} />
            <InputSwitchControl
                control={control}
                name="showVideo"
                defaultValue={values?.position?.companyName}
                title="Ajouter vos vidéo de présentation"
                disabled={!brandVideos || !showInfo}
                onChange={val => setShowVideo(val)}
                endAdornment={
                    <Popover hover>
                        <Typography fontWeight={700}>
                            Afficher la vidéo de présentation
                        </Typography>
                        <Typography variant="body2">
                            Passez d'une offre classique à une offre augmentée en proposant une vidéo de
                            présentation de votre société.
                        </Typography>
                        <Typography variant="body2">
                            Développez l'intérêt des talents pour votre offre en leurs faisant découvrir leur lieu
                            de travail, l'ambiance et les équipes.
                        </Typography>
                        <Typography variant="body2">Une offre augmentée génère jusqu'à 30% de vues en plus.</Typography>
                    </Popover>
                }
            />
            {showVideo && brandVideos?.map(video => <VideoContainer key={video._id} url={video?.url} />)}
            <AutoCompleteTalentProfessions
                allowAdd
                label="Fonction"
                placeholder="Sélectionnez la fonction recherchée"
                groupedOptions={options?.groupProfessions}
                control={control}
                name="position.professions"
                defaultValue={values?.position?.professions}
                multiple={true}
                rules={{
                    required: { value: true },
                }}
            />

            <InputMultiSelect
                label="Où sera exercée l’activité"
                placeholder="Sélectionnez les concernés"
                items={options?.places}
                control={control}
                name="position.place"
                defaultValue={values?.position?.place}
                required
            />

            <InputFieldControlled
                label="Postes disponibles"
                placeholder="Ex: 2"
                name="position.numberOfPositions"
                control={control}
                disabled={!pro?.isGroup}
                keyboardType="number-pad"
                defaultValue={values?.position?.numberOfPositions?.toString()}
                required
                rules={{
                    minMax: (val: number) => {
                        return (val >= 1 && val <= 100) || 'Le nombre de postes doit être compris entre 1 et 100';
                    },
                }}
            />
            <InputSwitchControl
                control={control}
                name="position.allowFreshGrad"
                defaultValue={values?.position?.allowFreshGrad}
                title="Débutant(e) accepté(e)"
                style={{ alignItems: 'center' }}
                onChange={val => {
                    setAllowFreshGrad(val);
                }}
            />

            <InputMultiSelect
                label="Nombre d'années d’expérience mini"
                placeholder="Sélectionnez l'expérience"
                items={options?.experiences}
                control={control}
                single
                name="position.experience"
                defaultValue={values?.position?.experience}
                required={allowFreshGrad === false}
            />
            <InputSwitchControl
                control={control}
                name="position.hasTeam"
                defaultValue={values?.position?.hasTeam}
                title="Management d’équipe"
                style={{ alignItems: 'center' }}
                onChange={value => {
                    setHasTeam(value);
                }}
            />
            {hasTeam && (
                <InputFieldControlled
                    name="position.teamNumber"
                    control={control}
                    keyboardType="number-pad"
                    label="Effectif à encadrer"
                    placeholder="Effectif"
                    defaultValue={values?.position?.teamNumber?.toString()}
                    rules={{
                        requiredIfTrue: val => {
                            return hasTeam ? !!val : true;
                        },
                    }}
                />
            )}
        </>
    );
};

export default PositionForm;
